<script lang="ts" setup>
/**
 * @file OptimizedTooltipText コンポーネント
 * パフォーマンスを最適化したツールチップ表示コンポーネント
 * DOM要素の動的切り替えを避け、単一要素で条件付きツールチップを実装
 */

import { ref, onMounted, onBeforeUnmount, computed, nextTick } from 'vue';

/** コンテンツ要素への参照 */
const cellContentRef = ref<HTMLDivElement | null>(null);

/** ツールチップを出すかどうかのフラグ */
const isToolTip = ref(false);

/** 初期化フラグ */
const isInitialized = ref(false);

/** スロットの変更を検出するためのカウンター */
const contentChangeCounter = ref(0);

/**
 * 要素が省略表示されているかをチェックし、ツールチップの表示フラグを更新
 * デバウンス処理付きで実行頻度を制限
 */
let updateTimeoutId: number | null = null;
/**
 * ツールチップの更新処理
 * 要素のサイズ変更やコンテンツ変更に応じてツールチップの表示状態を更新
 */
const updateTooltip = () => {
  if (updateTimeoutId) {
    clearTimeout(updateTimeoutId);
  }

  updateTimeoutId = setTimeout(() => {
    const el = cellContentRef.value;
    if (!el) return;

    // 要素が実際にDOMに配置されているかチェック
    if (el.offsetParent === null && el.offsetWidth === 0 && el.offsetHeight === 0) {
      return;
    }

    const needsTooltip = el.scrollWidth > el.clientWidth;
    if (isToolTip.value !== needsTooltip) {
      isToolTip.value = needsTooltip;
    }
  }, 16); // 約60FPSに制限
};

/**
 * ツールチップテキストの取得（キャッシュ付き）
 */
const toolTipText = computed(() => {
  contentChangeCounter.value;
  const el = cellContentRef.value;
  if (!el || !isToolTip.value) return '';
  return el.textContent || '';
});

/**
 * 遅延初期化：マウント後に非同期でツールチップ判定を実行
 */
onMounted(() => {
  nextTick(() => {
    isInitialized.value = true;
    updateTooltip();
  });

  // リサイズイベントはパッシブリスナーで追加
  window.addEventListener('resize', updateTooltip, { passive: true });
});

onBeforeUnmount(() => {
  if (updateTimeoutId) {
    clearTimeout(updateTimeoutId);
  }
  window.removeEventListener('resize', updateTooltip);
});

/**
 * 外部からコンテンツ変更を通知するためのメソッド
 * 親コンポーネントから明示的に呼び出される場合のみ実行
 */
const notifyContentChange = () => {
  contentChangeCounter.value++;
  if (isInitialized.value) {
    updateTooltip();
  }
};

// 外部からアクセス可能なメソッドを公開
defineExpose({
  notifyContentChange,
  updateTooltip,
});
</script>

<template>
  <!-- 単一のDOM要素で条件付きツールチップを実装 -->
  <div ref="cellContentRef" v-tooltip.top="isToolTip ? toolTipText : undefined" class="content">
    <slot />
  </div>
</template>

<style scoped lang="scss">
.content {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
